import { VISIBLE_LIMIT } from '../constants';
import type { ChildrenPaginationInfo, RichTreeItem } from '../types';
import { isPlaceholder, showMoreId } from './treeId';

/**
 * Walks the tree and appends a synthetic "Show more" leaf item to every group
 * that needs one.
 *
 * Per-parent expansion model:
 *  - Collapsed (default): we show the first `VISIBLE_LIMIT` items only.
 *  - Expanded: we show every locally loaded item.
 *
 * Button visibility rules:
 *  - "Show more" — there are more items to reveal (locally or on server).
 */
export function applyChildrenLimit(
  items: RichTreeItem[],
  expandedParents: Set<string>,
  paginationByParent: Record<string, ChildrenPaginationInfo> = {}
): RichTreeItem[] {
  return items.map((item) => {
    if (!item.children || item.children.length === 0) return item;
    if (item.children.length === 1 && isPlaceholder(item.children[0].id)) return item;

    const isExpanded = expandedParents.has(item.id);
    const limit = isExpanded ? item.children.length : VISIBLE_LIMIT;
    const visible = item.children.slice(0, limit);
    const hasMoreLocal = item.children.length > limit;
    const hasMoreOnServer = paginationByParent[item.id]?.hasMore ?? false;
    const hasMore = hasMoreLocal || hasMoreOnServer;

    const limitedChildren = applyChildrenLimit(visible, expandedParents, paginationByParent);

    if (hasMore) {
      limitedChildren.push({
        id: showMoreId(item.id),
        dataNodeId: '',
        title: '',
        autoId: '',
        label: 'Show more…'
      });
    }

    return { ...item, children: limitedChildren };
  });
}
